import { humanizeVectorStoreError } from './collectionCheck';
import { sanitizeErrorMessage } from './sanitizeErrorMessage';

const normalize = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

export const ERROR_CONTEXT = {
  KNOWLEDGE_SOURCE_VERIFY: 'knowledge_source_verify',
  KNOWLEDGE_SOURCE_SAVE: 'knowledge_source_save',
  KNOWLEDGE_SOURCE_UPLOAD: 'knowledge_source_upload',
  KNOWLEDGE_SOURCE_DELETE: 'knowledge_source_delete',
  PROJECT_CREATE: 'project_create',
  PROJECT_DELETE: 'project_delete',
  PROJECT_UPDATE: 'project_update',
  PIPELINE_RUN: 'pipeline_run',
  NEO4J_PUSH: 'neo4j_push',
  NEO4J_REMOVE: 'neo4j_remove',
  REBUILD_VECTORS: 'rebuild_vectors',
  RETENTION: 'retention',
  SCHEDULE: 'schedule',
  S3_LIBRARY: 's3_library',
  FILE_DOWNLOAD: 'file_download',
  FILE_DELETE: 'file_delete',
  FILE_CLEANUP: 'file_cleanup',
  CONFLUENCE_VERIFY: 'confluence_verify',
  GITHUB_VERIFY: 'github_verify',
};

/**
 * Normalize API error payloads (string, FastAPI validation array, object) to plain text.
 */
export function coerceErrorDetail(detailRaw) {
  if (detailRaw == null) return '';
  if (typeof detailRaw === 'string') return normalize(detailRaw);
  if (Array.isArray(detailRaw)) {
    return normalize(
      detailRaw
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item.msg === 'string') return item.msg;
          if (item && typeof item.message === 'string') return item.message;
          return '';
        })
        .filter(Boolean)
        .join(' '),
    );
  }
  if (typeof detailRaw === 'object') {
    if (typeof detailRaw.msg === 'string') return normalize(detailRaw.msg);
    if (typeof detailRaw.message === 'string') return normalize(detailRaw.message);
    if (typeof detailRaw.detail === 'string') return normalize(detailRaw.detail);
    try {
      return normalize(JSON.stringify(detailRaw));
    } catch {
      return normalize(String(detailRaw));
    }
  }
  return normalize(String(detailRaw));
}

export const isKnowledgeSourcePermissionError = (detail) => {
  const msg = coerceErrorDetail(detail).toLowerCase();
  return (
    msg.includes('accessdenied')
    || msg.includes('not authorized')
    || msg.includes('not authorized to perform')
    || msg.includes('s3:putobject')
    || msg.includes('s3:getobject')
    || msg.includes('s3:listbucket')
    || msg.includes('s3:deleteobject')
    || (msg.includes('failed to access s3 bucket') && msg.includes('403'))
  );
};

const invalidBucketName = (lower) => (
  lower.includes('invalid bucket name')
  || lower.includes('bucket name must match')
  || (lower.includes('parameter validation failed') && lower.includes('bucket'))
  || (lower.includes('failed to access s3 bucket') && lower.includes('parameter validation failed'))
);

/**
 * Knowledge Source / S3 configuration and file-access errors.
 */
export function humanizeKnowledgeSourceError(detailRaw, {
  fallback = 'Unable to verify Knowledge Source settings. Please check source name and region.',
} = {}) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return fallback;

  if (detail.includes('Failed to access S3 bucket') && detail.includes('403')) {
    return 'Unable to access this Knowledge Source. Please check the source name, region, and that your AWS credentials have at least read (HeadBucket/List) access.';
  }
  if (detail.includes('already registered to another project')) {
    return 'This Knowledge Source is already linked to another project. Each project must use a unique source.';
  }
  if (invalidBucketName(detail.toLowerCase())) {
    return 'That name is not a valid bucket. It cannot contain spaces or other disallowed characters. Use only letters, numbers, dots, hyphens, and underscores, between 3 and 63 characters, exactly as named in the AWS console.';
  }
  if (isKnowledgeSourcePermissionError(detail)) {
    return 'You do not have permission to access this Knowledge Source. Check AWS credentials and bucket policy.';
  }
  if (detail.toLowerCase().includes('nosuchkey') || detail.toLowerCase().includes('not found')) {
    return 'The file could not be found in the Knowledge Source bucket.';
  }

  return sanitizeErrorMessage(detail, { fallback });
}

export function humanizeConfluenceError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  const lower = detail.toLowerCase();
  if (!detail) {
    return 'Failed to verify Confluence configuration. Please check site URL, credentials, and space.';
  }
  if (lower.includes('unauthorized') || lower.includes('401')) {
    return 'Could not sign in to Confluence. Check your credentials and site URL.';
  }
  if (lower.includes('403') || lower.includes('forbidden')) {
    return 'Confluence denied access. Check that the account can read the configured space.';
  }
  if (lower.includes('space') && (lower.includes('not found') || lower.includes('does not exist'))) {
    return 'The configured Confluence space could not be found. Check the space key.';
  }
  if (lower.includes('already registered') || lower.includes('already linked')) {
    return 'This Confluence configuration is already linked to another project.';
  }
  return sanitizeErrorMessage(
    detail.replace(/\bS3\b/gi, 'Confluence').replace(/knowledge source/gi, 'Confluence'),
    { fallback: 'Failed to verify Confluence configuration. Please check site URL, credentials, and space.' },
  );
}

export function humanizeGithubError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  const lower = detail.toLowerCase();
  if (!detail) {
    return 'Failed to verify GitHub configuration. Please check repository URL and credentials.';
  }
  if (lower.includes('unauthorized') || lower.includes('401') || lower.includes('bad credentials')) {
    return 'Could not sign in to GitHub. Check your personal access token or app credentials.';
  }
  if (lower.includes('403') || lower.includes('forbidden')) {
    return 'GitHub denied access. Check that the token can read the configured repository.';
  }
  if (lower.includes('not found') && (lower.includes('repository') || lower.includes('repo'))) {
    return 'The configured GitHub repository could not be found. Check the repository URL.';
  }
  if (lower.includes('already registered') || lower.includes('already linked')) {
    return sanitizeErrorMessage(detail, {
      fallback: 'This GitHub configuration is already linked to another project.',
    });
  }
  return sanitizeErrorMessage(detail, {
    fallback: 'Failed to verify GitHub configuration. Please check repository URL and credentials.',
  });
}

export function humanizeProjectCreateError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not create the project. Please try again.';
  const lower = detail.toLowerCase();
  if (lower.includes('collection') && lower.includes('already exists')) {
    return 'That search index name is already in use. Choose a different collection name.';
  }
  if (lower.includes('already registered to another project')) {
    return humanizeKnowledgeSourceError(detail);
  }
  if (invalidBucketName(lower)) {
    return humanizeKnowledgeSourceError(detail);
  }
  return sanitizeErrorMessage(detail, { fallback: 'Could not create the project. Please try again.' });
}

export function humanizeProjectDeleteError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not delete the project. Please try again.';
  const lower = detail.toLowerCase();
  if (isKnowledgeSourcePermissionError(detail) || (lower.includes('empty') && lower.includes('bucket'))) {
    return 'Project was deleted, but some files could not be removed from the Knowledge Source bucket. Check permissions and try again.';
  }
  return sanitizeErrorMessage(detail, { fallback: 'Could not delete the project. Please try again.' });
}

export function humanizeConfigSaveError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not save your changes. Please try again.';
  const lower = detail.toLowerCase();
  if (lower.includes('verify') && (lower.includes('s3') || lower.includes('knowledge source'))) {
    return 'Please verify Knowledge Source configuration before saving.';
  }
  if (lower.includes('collection') && lower.includes('already exists')) {
    return 'That search index name is already in use. Choose a different collection name.';
  }
  if (lower.includes('github') && lower.includes('already linked')) {
    return sanitizeErrorMessage(detail, {
      fallback: 'This GitHub repository is already linked to another project.',
    });
  }
  if (lower.includes('s3') || lower.includes('bucket') || lower.includes('knowledge source')) {
    return humanizeKnowledgeSourceError(detail, { fallback: 'Could not save Knowledge Source settings.' });
  }
  return sanitizeErrorMessage(detail, { fallback: 'Could not save your changes. Please try again.' });
}

export function humanizePipelineRunError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not start file processing. Please try again.';
  const lower = detail.toLowerCase();
  if (lower.includes('already running') || lower.includes('lock')) {
    return 'Files are already being processed for this project. Please wait for the current run to finish.';
  }
  if (lower.includes('no files') || lower.includes('nothing to process')) {
    return 'No files are ready to process. Upload or sync files first.';
  }
  return sanitizeErrorMessage(detail, { fallback: 'Could not start file processing. Please try again.' });
}

export function humanizeNeo4jError(detailRaw, { action = 'push' } = {}) {
  const detail = coerceErrorDetail(detailRaw);
  const verb = action === 'remove' ? 'remove files from' : 'add files to';
  if (!detail) {
    return action === 'remove'
      ? 'Could not remove files from the knowledge graph (Neo4j).'
      : 'Could not add files to the knowledge graph (Neo4j).';
  }
  const lower = detail.toLowerCase();
  if (lower.includes('not configured') || lower.includes('ingest_endpoint')) {
    return 'Knowledge graph (Neo4j) is not configured for this project.';
  }
  if (lower.includes('timeout') || lower.includes('timed out')) {
    return `Timed out while trying to ${verb} the knowledge graph. Try again with fewer files.`;
  }
  if (looksLikeConnectionError(lower)) {
    return `The knowledge graph service could not be reached while trying to ${verb} Neo4j.`;
  }
  return sanitizeErrorMessage(detail, {
    fallback: action === 'remove'
      ? 'Could not remove files from the knowledge graph (Neo4j).'
      : 'Could not add files to the knowledge graph (Neo4j).',
  });
}

export function humanizeRebuildVectorsError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not rebuild the search index from saved data.';
  const lower = detail.toLowerCase();
  if (lower.includes('milvus') || lower.includes('vector')) {
    return humanizeVectorStoreError(detail, 'milvus');
  }
  if (lower.includes('cache') && lower.includes('not found')) {
    return 'No saved chunk data was found to rebuild the search index.';
  }
  return sanitizeErrorMessage(detail, { fallback: 'Could not rebuild the search index from saved data.' });
}

export function humanizeRetentionError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  return sanitizeErrorMessage(detail, { fallback: 'Retention cleanup could not be completed.' });
}

export function humanizeScheduleError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  return sanitizeErrorMessage(detail, { fallback: 'Could not update the processing schedule.' });
}

export function humanizeS3LibraryError(detailRaw, { action = 'list' } = {}) {
  const detail = coerceErrorDetail(detailRaw);
  if (isKnowledgeSourcePermissionError(detail)) {
    if (action === 'upload') {
      return 'Upload failed because this account does not have write access to the bucket.';
    }
    if (action === 'permissions') {
      return 'Could not verify bucket permissions for this account.';
    }
    return 'You do not have permission to list files in this bucket.';
  }
  const fallbacks = {
    buckets: 'Could not load project Knowledge Source buckets.',
    list: 'Could not load files from the Knowledge Source.',
    upload: 'Could not upload the file to the Knowledge Source.',
    permissions: 'Could not check Knowledge Source permissions.',
  };
  return humanizeKnowledgeSourceError(detail, {
    fallback: fallbacks[action] || fallbacks.list,
  });
}

export function humanizeFileDownloadError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not download the file.';
  if (isKnowledgeSourcePermissionError(detail)) {
    return 'You do not have permission to download this file from the Knowledge Source.';
  }
  return humanizeKnowledgeSourceError(detail, { fallback: 'Could not download the file.' });
}

export function humanizeFileDeleteError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) return 'Could not delete the file.';
  return humanizeKnowledgeSourceError(detail, { fallback: 'Could not delete the file.' });
}

export function humanizeFileCleanupError(detailRaw) {
  const detail = coerceErrorDetail(detailRaw);
  return sanitizeErrorMessage(detail, {
    fallback: 'File removal started, but indexed data cleanup could not be completed.',
  });
}

export function humanizeCollectionExistsError(collectionName, formattedName, vectorStoreType = 'milvus') {
  const store = String(vectorStoreType || 'milvus');
  const name = normalize(collectionName) || 'that name';
  const formatted = normalize(formattedName);
  const formattedText = formatted ? ` (formatted as "${formatted}")` : '';
  return `Cannot save: Collection name "${name}"${formattedText} already exists in ${store}. Please choose a different name.`;
}

const looksLikeConnectionError = (lower) => (
  lower.includes('httpconnectionpool')
  || lower.includes('connection refused')
  || lower.includes('failed to establish')
  || lower.includes('max retries exceeded')
  || lower.includes('timed out')
  || lower.includes('server unavailable')
);

/**
 * Domain-agnostic patterns for API errors without explicit context.
 */
export function humanizeGenericApiError(detailRaw, { fallback } = {}) {
  const detail = coerceErrorDetail(detailRaw);
  if (!detail) {
    return fallback || 'Something went wrong. Please try again.';
  }
  const lower = detail.toLowerCase();

  if (
    lower.includes('not authenticated')
    || lower.includes('could not validate credentials')
    || lower.includes('invalid token')
  ) {
    return 'Your session has expired. Please sign in again.';
  }
  if (lower.includes('forbidden') && !lower.includes('s3')) {
    return 'You do not have permission to perform this action.';
  }
  if (lower.includes('collection') && lower.includes('already exists')) {
    return 'That search index name is already in use. Choose a different collection name.';
  }
  if (lower.includes('milvus') || lower.includes('pymilvus')) {
    return humanizeVectorStoreError(detail, 'milvus');
  }
  if (lower.includes('graphrag') || lower.includes('neo4j')) {
    return humanizeNeo4jError(detail);
  }
  if (lower.includes('s3') || lower.includes('bucket') || lower.includes('knowledge source')) {
    return humanizeKnowledgeSourceError(detail);
  }

  return sanitizeErrorMessage(detail, { fallback: fallback || 'Something went wrong. Please try again.' });
}

/**
 * Main entry point — pass an Axios error or raw API detail with optional context.
 */
export function humanizeApiError(errorOrDetail, {
  fallback,
  context,
  ...contextOptions
} = {}) {
  const detail = (
    errorOrDetail?.response?.data?.detail != null
      ? errorOrDetail.response.data.detail
      : errorOrDetail?.response?.data?.message != null
        ? errorOrDetail.response.data.message
        : errorOrDetail
  );

  let message = '';
  switch (context) {
    case ERROR_CONTEXT.KNOWLEDGE_SOURCE_VERIFY:
    case ERROR_CONTEXT.KNOWLEDGE_SOURCE_SAVE:
    case ERROR_CONTEXT.KNOWLEDGE_SOURCE_UPLOAD:
    case ERROR_CONTEXT.KNOWLEDGE_SOURCE_DELETE:
      message = humanizeKnowledgeSourceError(detail, { fallback, ...contextOptions });
      break;
    case ERROR_CONTEXT.CONFLUENCE_VERIFY:
      message = humanizeConfluenceError(detail);
      break;
    case ERROR_CONTEXT.GITHUB_VERIFY:
      message = humanizeGithubError(detail);
      break;
    case ERROR_CONTEXT.PROJECT_CREATE:
      message = humanizeProjectCreateError(detail);
      break;
    case ERROR_CONTEXT.PROJECT_DELETE:
      message = humanizeProjectDeleteError(detail);
      break;
    case ERROR_CONTEXT.PROJECT_UPDATE:
      message = humanizeConfigSaveError(detail);
      break;
    case ERROR_CONTEXT.PIPELINE_RUN:
      message = humanizePipelineRunError(detail);
      break;
    case ERROR_CONTEXT.NEO4J_PUSH:
      message = humanizeNeo4jError(detail, { action: 'push', ...contextOptions });
      break;
    case ERROR_CONTEXT.NEO4J_REMOVE:
      message = humanizeNeo4jError(detail, { action: 'remove', ...contextOptions });
      break;
    case ERROR_CONTEXT.REBUILD_VECTORS:
      message = humanizeRebuildVectorsError(detail);
      break;
    case ERROR_CONTEXT.RETENTION:
      message = humanizeRetentionError(detail);
      break;
    case ERROR_CONTEXT.SCHEDULE:
      message = humanizeScheduleError(detail);
      break;
    case ERROR_CONTEXT.S3_LIBRARY:
      message = humanizeS3LibraryError(detail, contextOptions);
      break;
    case ERROR_CONTEXT.FILE_DOWNLOAD:
      message = humanizeFileDownloadError(detail);
      break;
    case ERROR_CONTEXT.FILE_DELETE:
      message = humanizeFileDeleteError(detail);
      break;
    case ERROR_CONTEXT.FILE_CLEANUP:
      message = humanizeFileCleanupError(detail);
      break;
    default:
      message = humanizeGenericApiError(detail, { fallback });
  }

  return sanitizeErrorMessage(message, {
    fallback: fallback || 'Something went wrong. Please try again.',
  });
}
